import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import InventoryShell from "./InventoryShell";

/**
 * Inventory Dashboard — Phase I1.
 *
 * Read-only summary of the boutique's inventory posture. All values derived
 * from Toto-owned collections; no Zenoti dependency. Actions link into I2+
 * workflows (Retail Audit is live; Receiving / Variances / Trends land later).
 */

function ProgressRing({ pct = 0, size = 56 }) {
  const r = (size - 4) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EAE7DE" strokeWidth="2" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#1F2937"
        strokeWidth="2"
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

function relativeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const days = Math.round((Date.now() - then) / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.round(days / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}

export default function InventoryDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/inventory/dashboard");
        if (alive) setData(r.data);
      } catch (e) {
        console.error("inv dashboard load", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const openSession = data?.open_session;
  const daysSince = data?.last_completed?.days_since;
  const daysUntil = data?.next_due?.days_until ?? 0;
  const nextDueLabel = daysUntil < 0
    ? `overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? "" : "s"}`
    : daysUntil === 0 ? "due today"
    : `in ${daysUntil} day${daysUntil === 1 ? "" : "s"}`;

  return (
    <InventoryShell title="Inventory">
      {loading ? (
        <div className="text-sm text-stone-400">Loading…</div>
      ) : (
        <>
          {/* Top strip: two Quiet-Glass cards */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Audit Status Card */}
            <div
              className="rounded-2xl border border-stone-100 bg-white p-6"
              data-testid="inv-dash-status-card"
            >
              <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400 mb-1">
                Last completed
              </div>
              {data?.last_completed?.closed_at ? (
                <>
                  <div className="font-serif-display text-2xl text-stone-900">
                    {relativeAgo(data.last_completed.closed_at)}
                  </div>
                  <div className="text-xs text-stone-500 mt-1">
                    Closed by {data.last_completed.closed_by_name || "—"}
                    {daysSince != null ? ` · ${daysSince} days ago` : ""}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-serif-display text-2xl text-stone-900">
                    No audits yet
                  </div>
                  <div className="text-xs text-stone-500 mt-1">
                    This will be your boutique's first audit inside Toto.
                  </div>
                </>
              )}
            </div>

            {/* Next Due Card */}
            <div
              className="rounded-2xl border border-stone-100 bg-white p-6 flex items-center gap-4"
              data-testid="inv-dash-next-due-card"
            >
              <ProgressRing
                pct={
                  daysUntil <= 0 ? 100 :
                  Math.max(0, Math.min(100, 100 - (daysUntil / 30) * 100))
                }
              />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400 mb-1">
                  Next audit
                </div>
                <div className="font-serif-display text-2xl text-stone-900">
                  {nextDueLabel}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  {data?.total_products ?? 0} SKUs · est. 45 min
                </div>
              </div>
              <button
                onClick={() => navigate("/operations/inventory/audit")}
                data-testid="inv-dash-start-audit"
                className="shrink-0 rounded-full bg-stone-900 text-white text-sm px-4 py-2 hover:bg-stone-700 transition-colors"
              >
                {openSession ? "Resume" : "Start audit"}
              </button>
            </div>
          </section>

          {/* Read-only posture: each live task is reachable from the concise
              Inventory navigation above, so this surface does not repeat it. */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <EmptyCard
              testid="inv-dash-variances-list"
              label="Outstanding variances"
              empty="No open variances."
              phase="I3"
            />
            <EmptyCard
              testid="inv-dash-deliveries-list"
              label="Recent deliveries"
              empty="No deliveries recorded yet."
              phase="I4"
            />
            <EmptyCard
              testid="inv-dash-noticing"
              label="Toto is noticing"
              empty="Not enough audits yet to spot a pattern."
              phase="I7"
            />
          </section>
        </>
      )}
    </InventoryShell>
  );
}

function EmptyCard({ label, empty, phase, testid }) {
  return (
    <div className="rounded-2xl border border-stone-100 bg-white p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-medium uppercase tracking-wider text-stone-400">
          {label}
        </div>
        <span className="text-[9px] uppercase tracking-wider text-stone-300">Phase {phase}</span>
      </div>
      <div className="text-sm text-stone-500">{empty}</div>
    </div>
  );
}
